import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getSocket, subscribeToRealtimeEvent } from './socketClient'
import { REALTIME_EVENTS, type RealtimeEvent } from './socketEvents'
import { DASHBOARD_QUERY_KEYS } from '../../features/admin/dashboard/constants'
import { ADMISSIONSDASHBOARD_QUERY_KEYS } from '../../features/admissions/dashboard/constants'
import { FEE_PAYMENTS_QUERY_KEY } from '../../features/finance/fees-finance/hooks/useFeePayments'
import { DASHBOARD_KPI_KEYS } from '../hooks/useDashboardKpis'

export interface RealtimeSyncOptions {
  /** Optional custom handler map for specific events */
  onEvent?: Partial<Record<RealtimeEvent, (payload: unknown) => void>>
}

/**
 * Custom React hook that connects to Socket.io and automatically keeps
 * frontend React Query caches synchronized with backend realtime mutations.
 */
export function useRealtimeSync(options?: RealtimeSyncOptions) {
  const queryClient = useQueryClient()
  const [isConnected, setIsConnected] = useState(false)
  const [lastEvent, setLastEvent] = useState<{ event: string; timestamp: Date; payload?: unknown } | null>(null)

  useEffect(() => {
    const socket = getSocket()
    if (!socket) return

    const handleConnect = () => setIsConnected(true)
    const handleDisconnect = () => setIsConnected(false)

    if (socket.connected) {
      setIsConnected(true)
    }

    socket.on('connect', handleConnect)
    socket.on('disconnect', handleDisconnect)

    // Helper to invalidate and record last event
    const handleEvent = (event: RealtimeEvent, invalidations: () => void, payload?: unknown) => {
      setLastEvent({ event, timestamp: new Date(), payload })
      invalidations()
      options?.onEvent?.[event]?.(payload)
    }

    const unsubs = [
      // 1. Admin Dashboard KPI & Aggregator updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.DASHBOARD_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DASHBOARD_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.GET })
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.ADMIN_AGGREGATOR })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.DASHBOARD_ADMIN_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DASHBOARD_ADMIN_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.ADMIN_AGGREGATOR })
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.GET })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.DASHBOARD_STUDENT_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DASHBOARD_STUDENT_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.STUDENT_AGGREGATOR })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.DASHBOARD_STAFF_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DASHBOARD_STAFF_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.STAFF_AGGREGATOR })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.DASHBOARD_ALL_KPIS_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DASHBOARD_ALL_KPIS_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['dashboard', 'kpis'] })
        }, payload)
      }),

      // 2. Domain-Specific KPI Updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.STUDENT_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.STUDENT_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.STUDENT_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.STAFF_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.STAFF_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.STAFF_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.SEAT_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.SEAT_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.SEAT_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.ACADEMIC_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.ACADEMIC_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.ACADEMIC_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.SUBJECT_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.SUBJECT_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.SUBJECT_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.USER_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.USER_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.USER_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.ROLE_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.ROLE_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.ROLE_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.DEPARTMENT_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DEPARTMENT_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.DEPARTMENT_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.DESIGNATION_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DESIGNATION_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.DESIGNATION_KPIS })
        }, payload)
      }),

      // 3. Attendance updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.DASHBOARD_ATTENDANCE_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DASHBOARD_ATTENDANCE_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.GET })
          void queryClient.invalidateQueries({ queryKey: ['attendance'] })
        }, payload)
      }),

      // 4. Fee status & Payments updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.DASHBOARD_FEES_STATUS_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DASHBOARD_FEES_STATUS_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.GET })
          void queryClient.invalidateQueries({ queryKey: [FEE_PAYMENTS_QUERY_KEY] })
          void queryClient.invalidateQueries({ queryKey: ['feeReports'] })
          void queryClient.invalidateQueries({ queryKey: ['feeOverview'] })
        }, payload)
      }),

      // 5. Finance overview updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.FINANCE_OVERVIEW_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.FINANCE_OVERVIEW_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.GET })
          void queryClient.invalidateQueries({ queryKey: [FEE_PAYMENTS_QUERY_KEY] })
          void queryClient.invalidateQueries({ queryKey: ['feeReports'] })
          void queryClient.invalidateQueries({ queryKey: ['feeStructures'] })
        }, payload)
      }),

      // 6. Admissions KPI updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.ADMISSIONS_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.ADMISSIONS_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ADMISSIONSDASHBOARD_QUERY_KEYS.GET })
          void queryClient.invalidateQueries({ queryKey: ['admissions'] })
        }, payload)
      }),

      // 7. Pending approvals updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.DASHBOARD_APPROVALS_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DASHBOARD_APPROVALS_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.GET })
          void queryClient.invalidateQueries({ queryKey: ['approvals'] })
        }, payload)
      }),

      // 8. Recent activity & Notices updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.DASHBOARD_ACTIVITY_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.DASHBOARD_ACTIVITY_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.GET })
          void queryClient.invalidateQueries({ queryKey: ['notices'] })
        }, payload)
      }),

      // 9. Communication statistics updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.COMMUNICATION_STATS_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.COMMUNICATION_STATS_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['communications'] })
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.GET })
        }, payload)
      }),

      // 10. Assignments updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.ASSIGNMENTS_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.ASSIGNMENTS_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['assignments'] })
          void queryClient.invalidateQueries({ queryKey: ['teacherAssignments'] })
        }, payload)
      }),

      // 11. Student dashboard updates
      subscribeToRealtimeEvent(REALTIME_EVENTS.STUDENT_DASHBOARD_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.STUDENT_DASHBOARD_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['studentDashboard'] })
        }, payload)
      }),

      // 12. Organization Admin Scope Realtime Events
      subscribeToRealtimeEvent(REALTIME_EVENTS.ORGANIZATION_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.ORGANIZATION_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['organizationKpis'] })
          void queryClient.invalidateQueries({ queryKey: ['organizationDetails'] })
          void queryClient.invalidateQueries({ queryKey: ['organization', 'me'] })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.BRANCHES_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.BRANCHES_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['branches', 'kpis'] })
          void queryClient.invalidateQueries({ queryKey: ['organizationKpis'] })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.BRANCH_LIST_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.BRANCH_LIST_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['branches'] })
          void queryClient.invalidateQueries({ queryKey: ['branches', 'kpis'] })
          void queryClient.invalidateQueries({ queryKey: ['organizationKpis'] })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.ORG_ROLES_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.ORG_ROLES_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['roles'] })
          void queryClient.invalidateQueries({ queryKey: ['roles', 'kpis'] })
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.ROLE_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.ORG_USERS_KPI_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.ORG_USERS_KPI_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['users'] })
          void queryClient.invalidateQueries({ queryKey: ['users', 'kpis'] })
          void queryClient.invalidateQueries({ queryKey: DASHBOARD_KPI_KEYS.USER_KPIS })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.AUDIT_STATS_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.AUDIT_STATS_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['audit-logs'] })
          void queryClient.invalidateQueries({ queryKey: ['audit-logs-page'] })
          void queryClient.invalidateQueries({ queryKey: ['audit-logs-stats'] })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.AUDIT_LOG_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.AUDIT_LOG_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['audit-logs'] })
          void queryClient.invalidateQueries({ queryKey: ['audit-logs-page'] })
          void queryClient.invalidateQueries({ queryKey: ['audit-logs-stats'] })
        }, payload)
      }),

      subscribeToRealtimeEvent(REALTIME_EVENTS.ORG_DASHBOARD_UPDATED, (payload) => {
        handleEvent(REALTIME_EVENTS.ORG_DASHBOARD_UPDATED, () => {
          void queryClient.invalidateQueries({ queryKey: ['orgDashboard'] })
          void queryClient.invalidateQueries({ queryKey: ['branches', 'kpis'] })
          void queryClient.invalidateQueries({ queryKey: ['organizationKpis'] })
        }, payload)
      }),
    ]

    return () => {
      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
      unsubs.forEach((unsub) => unsub())
    }
  }, [queryClient, options])

  return {
    isConnected,
    lastEvent,
  }
}
